import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import moment from 'moment'

import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import BasicTile from 'src/designSystem/components/molecules/BasicTile/BasicTile'
import Button from 'src/designSystem/components/atoms/Button/Button'
import Box from 'src/designSystem/components/atoms/Box/Box'
import {
  LoanClosedReasonCode,
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '@possible/cassandra/src/types/types.mobile.generated'
import PFSvgContain from 'src/designSystem/components/atoms/PFSvg/PFSvgContain'
import SunIcon from 'src/assets/icons/sun.svg'
import MoneyRoundAbout from 'src/assets/icons/MoneyRoundAbout.svg'
import {DividerWithTextOrIcon} from 'src/designSystem/components/atoms/DividerWithTextOrIcon/DividerWithTextOrIcon'
import {AdvanceAvailableSubTile} from 'src/products/advance/Dashboard/MembershipCanceledTile/AdvanceAvailableSubTile'
import {MembershipAgreementBottomSheet} from 'src/products/advance/Dashboard/MembershipAgreementBottomSheet/MembershipAgreementBottomSheet'
import {MembershipReactivatedBottomSheet} from 'src/products/advance/Dashboard/MembershipReactivatedBottomSheet/MembershipReactivatedBottomSheet'
import {logAndShowErrorMessage} from 'src/lib/errors'

export type MembershipCanceledTileProps = {
  testID: string
  onReactivateMembership: () => Promise<void>
  onGetAdvance?: () => Promise<void>
  subscriptionStatus?: SubscriptionStatus
  subscriptionSubStatus?: SubscriptionSubstatus
  finalMembershipFeeDate?: string
  prequalAmount?: string
  assessmentDate?: string
  loanId: string
  onAcceptSubscriptionAgreement?: () => Promise<void>
  refetchDashboardData: () => Promise<void>
  isResumeMembershipLoading: boolean
  reason?: LoanClosedReasonCode
}

/**
 * A tile to inform the user that their membership has been canceled.
 * Includes an action button to reactivate the membership.
 * @example <MembershipCanceledTile onResumeMembership={() => foo()} subscriptionStatus={SubscriptionStatus.Cancelled} subscriptionSubStatus={SubscriptionSubstatus.CancelledPaused} finalMembershipFeeDate="October 30th"/>
 */
const MembershipCanceledTile: React.FC<MembershipCanceledTileProps> = ({
  testID,
  onReactivateMembership: handleOnReactivateMembership,
  onGetAdvance: handleOnGetAdvance,
  subscriptionStatus,
  subscriptionSubStatus,
  finalMembershipFeeDate,
  prequalAmount,
  assessmentDate,
  loanId,
  onAcceptSubscriptionAgreement,
  refetchDashboardData,
  isResumeMembershipLoading,
  reason,
}: MembershipCanceledTileProps) => {
  const {t} = useTranslation('DashboardMembershipCanceledTile')
  const [shouldViewAgreementBottomSheet, setShouldViewAgreementBottomSheet] =
    useState<boolean>(false)
  const [shouldViewReactivatedBottomSheet, setShouldViewReactivatedBottomSheet] =
    useState<boolean>(false)
  const [isSelected, setIsSelected] = useState<boolean>(false)

  const handleSetSelected = useCallback((value: boolean) => {
    setIsSelected(value)
  }, [])

  const handleCloseAgreementBottomSheet = useCallback(() => {
    setShouldViewAgreementBottomSheet(false)
  }, [])

  const handleCloseReactivatedBottomSheet = useCallback(async () => {
    await refetchDashboardData()
    setShouldViewReactivatedBottomSheet(false)
  }, [refetchDashboardData])

  const handleOpenReactivatedBottomSheet = useCallback(() => {
    setShouldViewReactivatedBottomSheet(true)
  }, [])

  const handleOpenAgreementBottomSheet = useCallback(() => {
    setShouldViewAgreementBottomSheet(true)
  }, [])

  const handleOnReactivateMembershipCTA = useCallback(async () => {
    if (
      reason === LoanClosedReasonCode.Paidoff &&
      subscriptionSubStatus === SubscriptionSubstatus.Cancelled
    ) {
      await handleOnGetAdvance?.()
    } else {
      handleOpenAgreementBottomSheet()
    }
  }, [handleOnGetAdvance, handleOpenAgreementBottomSheet, reason, subscriptionSubStatus])

  const formattedFinalMembershipFeeDate = moment(finalMembershipFeeDate).format('MMMM Do')

  const isMembershipPaused =
    subscriptionStatus === SubscriptionStatus.Cancelled &&
    subscriptionSubStatus === SubscriptionSubstatus.CancelledPaused

  const isMembershipPendingCancellation =
    subscriptionStatus === SubscriptionStatus.Active &&
    subscriptionSubStatus === SubscriptionSubstatus.ActivePendingCancellation

  const isMembershipCanceled =
    subscriptionStatus === SubscriptionStatus.Cancelled &&
    subscriptionSubStatus === SubscriptionSubstatus.Cancelled

  const isCanceled = isMembershipPendingCancellation || isMembershipCanceled

  const handleOnAcceptSubscriptionAgreement = useCallback(async () => {
    try {
      await onAcceptSubscriptionAgreement?.()
      await handleOnReactivateMembership()

      handleCloseAgreementBottomSheet()
      setTimeout(() => {
        handleOpenReactivatedBottomSheet()
      }, 1000)
    } catch (error) {
      logAndShowErrorMessage({error})
    }
  }, [
    handleCloseAgreementBottomSheet,
    handleOnReactivateMembership,
    handleOpenReactivatedBottomSheet,
    onAcceptSubscriptionAgreement,
  ])

  const handleAdvanceAvailableSubTileGetAdvance = useCallback(async () => {
    if (
      reason === LoanClosedReasonCode.Paidoff &&
      subscriptionSubStatus === SubscriptionSubstatus.Cancelled
    ) {
      await handleOnGetAdvance?.()
    } else {
      handleOpenAgreementBottomSheet()
    }
  }, [handleOnGetAdvance, handleOpenAgreementBottomSheet, reason, subscriptionSubStatus])

  const description = useMemo(() => {
    if (isMembershipCanceled) {
      return (
        <Box>
          <PFText variant="p">{t('WeHaveYourProgressTowardPotentialHigherAdvancesSaved')}</PFText>
        </Box>
      )
    }

    if (isMembershipPendingCancellation && formattedFinalMembershipFeeDate) {
      return (
        <Box direction="column" gap="small">
          <PFText variant="p">
            {t('YourMembershipFeeWillBePaidOn', {date: formattedFinalMembershipFeeDate})}
          </PFText>
          <PFText variant="p">{t('WeHaveYourProgressTowardPotentialHigherAdvancesSaved')}</PFText>
        </Box>
      )
    }

    if (isMembershipPaused) {
      return (
        <Box direction="column" gap="small">
          <PFText variant="p">{t('MembershipPausedDescription')}</PFText>
          <PFText variant="p">{t('ReactivateMembershipToRequestNewAdvance')}</PFText>
        </Box>
      )
    }

    return null
  }, [
    formattedFinalMembershipFeeDate,
    isMembershipCanceled,
    isMembershipPaused,
    isMembershipPendingCancellation,
    t,
  ])

  return (
    <>
      <BasicTile padding="medium" testID={testID} gap="small">
        <Box direction="row" justify="between">
          <Box gap="little">
            <PFText variant="p_lg_semibold">
              {isCanceled ? t('MembershipCanceled') : t('YourMembershipWasPaused')}
            </PFText>
          </Box>
          <Box>
            <PFSvgContain svg={isCanceled ? SunIcon : MoneyRoundAbout} width={46} height={46} />
          </Box>
        </Box>
        {description}
        <Box align="center" gap="small" fill="horizontal">
          <Button
            mode="primary"
            size="medium"
            onPress={handleOnReactivateMembershipCTA}
            width="100%"
            testID={`${testID}-ReActivateMembershipBtn`}
          >
            {t('ReactivateMembership')}
          </Button>
        </Box>
        {prequalAmount && parseFloat(prequalAmount) > 0 ? (
          <Box gap="small">
            <DividerWithTextOrIcon hasNoTextNoIcon />
            <AdvanceAvailableSubTile
              testID={`${testID}-Advance-Available-SubTile`}
              onGetAdvance={handleAdvanceAvailableSubTileGetAdvance}
              preQualAmount={prequalAmount}
              assessmentDate={assessmentDate}
            />
          </Box>
        ) : null}
      </BasicTile>
      <MembershipAgreementBottomSheet
        visible={shouldViewAgreementBottomSheet}
        onDismiss={handleOnAcceptSubscriptionAgreement}
        onSetSelected={handleSetSelected}
        loanId={loanId}
        isSelected={isSelected}
        onCloseBottomSheet={handleCloseAgreementBottomSheet}
        isResumeMembershipLoading={isResumeMembershipLoading}
      />
      <MembershipReactivatedBottomSheet
        visible={shouldViewReactivatedBottomSheet}
        onDismiss={handleCloseReactivatedBottomSheet}
        onCloseBottomSheet={handleCloseReactivatedBottomSheet}
      />
    </>
  )
}

export {MembershipCanceledTile}
